import { useTranslation } from 'react-i18next'

import type { Locale } from '@/shared/i18n'
import { LOCALES, setLocale } from '@/shared/i18n'
// Direct paths, not the barrel: this renders inside the eager app layout.
import { AnimatedIcon } from '@/shared/ui/animated-icon'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'
import { LanguagesIcon } from '@/shared/ui/icons/languages'
import { TOPBAR_PILL_TILE } from '@/shared/ui/topbar-pill'

/**
 * The language tile in the topbar pill.
 *
 * It was a two-state toggle showing `RU` / `EN`, which put the *other* locale
 * on screen and never said what the current one was. A radio menu states both:
 * the list is the choice, the mark is the answer. Names are endonyms — a
 * language is offered in its own writing or the person who needs it cannot
 * read the entry.
 */
export function LocaleMenu() {
  const { t, i18n } = useTranslation('common')
  const locale: Locale = i18n.resolvedLanguage === 'en' ? 'en' : 'ru'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <button
            type="button"
            title={t('actions.language')}
            aria-label={t('actions.language')}
            className={TOPBAR_PILL_TILE}
          >
            <AnimatedIcon icon={LanguagesIcon} />
          </button>
        }
      />
      <DropdownMenuContent align="end">
        <DropdownMenuRadioGroup
          value={locale}
          onValueChange={(value) => void setLocale(value as Locale)}
        >
          {LOCALES.map((value) => (
            <DropdownMenuRadioItem key={value} value={value}>
              {t(`locale.${value}`)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
